import { Component, Input, OnChanges, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { CompaniesService } from '../list-companies.service';
import { AdditionalCost } from 'src/app/core/interfaces/additionalCost';
import { SnackBarService } from 'src/app/core/services/snackBar.service';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { Utils } from 'src/app/core/resources/utils';
import { NgxSpinnerService } from 'ngx-spinner';
import { WhoToPay } from 'src/app/core/interfaces/whoToPay';
import { AuthService } from 'src/app/core/services/authentication.service';
import { ShippingCost } from 'src/app/core/enums/shipping-cost.enum';
import { environment } from 'src/environments/environment';
import { FormMessages } from 'src/app/core/messages/form-messages.enum';
import { ServiceMessages } from 'src/app/core/messages/service-messages.enum';
import { CompanyManager } from 'src/app/core/managers/company.manager';

@Component({
  selector: 'app-additional-services-config',
  templateUrl: './additional-services-config.component.html',
  styleUrls: ['./additional-services-config.component.scss']
})
export class AdditionalServicesConfigComponent implements OnChanges, OnDestroy {

  @Input() nitCompany: string;
  @Input() transportCompanyId: string;
  private _destroy$ = new Subject<void>();
  // Los datos los provee el componente padre (company-config) para que el
  // skeleton del tab cubra también esta carga; el hijo ya no consulta por su cuenta.
  @Input() additionalCosts: AdditionalCost[] = [];
  @Input() whoToPays: WhoToPay[] = [];
  listAdditionalCost: AdditionalCost[] = [];
  // Separados para la vista: utilidad general y utilidad por actor (utilityByActor).
  generalServices: AdditionalCost[] = [];
  byActorServices: AdditionalCost[] = [];
  listWhoToPays: WhoToPay[] = [];
  listWhoToPaysDefault: WhoToPay[] = [
    { id: 'D', name: 'DESTINATARIO' },
    { id: 'R', name: 'REMITENTE' },
    { id: 'C', name: 'CONDUCTOR' },
    { id: "E", name: "EMPRESA DE TRANSPORTE" }
  ];
  form: FormGroup = new FormGroup({});
  minUtility: number = 0;
  saving: boolean = false;

  constructor(
    private listCompaniesService: CompaniesService,
    private snackBarService: SnackBarService,
    public utils: Utils,
    public spinner: NgxSpinnerService,
    private authService: AuthService,
    private companyManager: CompanyManager
  ) { }

  ngOnChanges() {
    this.minUtility = this.companyManager.getutilityAdditionalServicesByCompanyId(this.nitCompany);
    this.listAdditionalCost = this.additionalCosts && this.additionalCosts.length ? this.additionalCosts : [];
    this.listWhoToPays = this.whoToPays && this.whoToPays.length ? this.whoToPays : this.listWhoToPaysDefault;
    this.initForm();
  }

  private initForm() {
    this.form = new FormGroup({});
    this.generalServices = this.listAdditionalCost.filter(service => !this.isByActor(service));
    this.byActorServices = this.listAdditionalCost.filter(service => this.isByActor(service));
    const defaultUtility = this.authService && this.authService.getUserSession() && this.authService.getUserSession().clientCompanyId && this.authService.getUserSession().clientCompanyId === environment.rootNit
      ? ShippingCost.MINIMUN_UTILITY_ADDITIONAL_COST_TECLOGI
      : ShippingCost.MINIMUN_UTILITY_ADDITIONAL_COST;
    this.listAdditionalCost.forEach((service: AdditionalCost) => {
      this.form.addControl(service.name, new FormGroup({}));
      const serviceControl = this.form.get(service.name) as FormGroup;

      if (this.isByActor(service)) {
        // Un campo por actor; si el servicio no trae el de algún actor, arranca con la utilidad por defecto.
        serviceControl.addControl("whoToPaysUtilities", new FormArray([]));
        this.listWhoToPays.forEach((who) => {
          const saved = (service.whoToPaysUtilities || []).find(whoC => whoC.whoToPayId === who.id);
          const utility = saved && this.utils.isDefined(saved.utility) ? saved.utility : defaultUtility;
          (serviceControl.get('whoToPaysUtilities') as FormArray).push(
            new FormGroup({
              "whoToPayId": new FormControl(who.id),
              "whoToPay": new FormControl(who.name),
              "utility": new FormControl(utility, [Validators.required, Validators.min(this.minUtility), Validators.max(100)]),
            }))
        })
      } else {
        let utility = this.utils.isDefined(service.utility) ? service.utility : defaultUtility;
        serviceControl.addControl("utility", new FormControl(utility, [Validators.required, Validators.min(this.minUtility), Validators.max(100)]));
      }
    })
  }

  isByActor(service: AdditionalCost): boolean {
    return !!(service && service.utilityByActor);
  }

  /** "EMPRESA DE TRANSPORTE" -> "Empresa de transporte". */
  actorLabel(name: string): string {
    const text = (name || '').toLowerCase();
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  getWhosByService(serviceName: string): FormArray {
    return this.form.get(serviceName + '.whoToPaysUtilities') as FormArray;
  }

  onSubmit() {
    for (const service of this.listAdditionalCost) {
      if (this.isByActor(service)) {
        const whos = this.getWhosByService(service.name);
        for (const who of (whos ? whos.controls : [])) {
          if (this.utils.errorMessagesCustomized(who.get('utility'), `utilidad de ${service.name} (${this.actorLabel(who.get('whoToPay').value)})`, null, null, this.minUtility, 100))
            return;
        }
      } else if (this.utils.errorMessagesCustomized(this.form.get(`${service.name}.utility`), `utilidad de ${service.name}`, null, null, this.minUtility, 100)) {
        return;
      }
    }
    this.saveUtilities();
  }

  /** Todos los servicios tal cual llegaron, con sus utilidades del formulario (por actor o la general). */
  private buildBody(): AdditionalCost[] {
    const formValue = this.form.getRawValue();
    return this.utils.clone(this.listAdditionalCost).map((service: AdditionalCost) => {
      if (this.isByActor(service)) service.whoToPaysUtilities = formValue[service.name]['whoToPaysUtilities'];
      else service.utility = formValue[service.name].utility;
      return service;
    });
  }

  /** Un solo PUT con todos los servicios. */
  private saveUtilities(): void {
    if (this.saving) return;
    const body = this.buildBody();
    this.saving = true;
    this.spinner.show();
    const services = this.listAdditionalCost;
    const transportCompanyId = this.transportCompanyId;
    this.listCompaniesService.saveAdditionalCostsUtilities(body, transportCompanyId).pipe(takeUntil(this._destroy$), finalize(() => {
      this.saving = false;
      this.spinner.hide();
    })).subscribe(
      () => {
        if (transportCompanyId !== this.transportCompanyId || services !== this.listAdditionalCost) return;
        // Lo guardado pasa a ser el estado de la lista que recibe el componente.
        body.forEach(saved => {
          const service = this.listAdditionalCost.find(item => item.id === saved.id);
          if (service) Object.assign(service, { utility: saved.utility, whoToPaysUtilities: saved.whoToPaysUtilities });
        });
        this.snackBarService.openSnackBar(ServiceMessages.ADDITIONAL_COSTS_UTILITIES_UPDATED);
      },
      (error) => {
        this.snackBarService.openSnackBar(error && error.error && error.error.message ? error.error.message : ServiceMessages.GENERAL_HTTP_ERROR, undefined, 'error');
      }
    );
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

}
